/* ============================================
   HostingNuevoServicio — alta de un paquete de servicio gestionado.
   Wizard de etapas (reemplaza el modal). El paquete es la unidad de
   cobro; los componentes (Cloudflare / Hetzner / Supabase / Verpex)
   son las piezas técnicas de corte. Se cancela el paquete, no las piezas.
   ============================================ */

import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { HostingComponente, HostingTipoPaquete, Cliente, ContactoVinculado } from '@/lib/types'
import { contactoFacturacionDe } from '@/lib/types'
import ComponentesBuilder, { nuevoComponente, PROVEEDOR_OPCIONES } from '@/components/hosting/ComponentesBuilder'
import SelectorContactoEmpresa, { nombreContacto } from '@/components/contactos/SelectorContactoEmpresa'
import { PageHeader } from '@/components/ui'

const PASOS = ['Cliente', 'Tipo', 'Comercial', 'Componentes', 'Cobranza', 'Resumen'] as const

const TIPO_CARDS: { value: HostingTipoPaquete; titulo: string; desc: string; icon: string }[] = [
  { value: 'hosting', titulo: 'Hosting', desc: 'Cuenta cPanel / web hosting clásico (un proveedor).', icon: '🌐' },
  { value: 'cloud', titulo: 'Cloud', desc: 'Infraestructura con varios componentes (Hetzner + Supabase + Cloudflare).', icon: '☁️' },
  { value: 'otro', titulo: 'Otro', desc: 'Servicio gestionado a medida.', icon: '⚙️' },
]

const emailValido = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)

export default function HostingNuevoServicio() {
  const api = useApi()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [paso, setPaso] = useState(0)
  const [saving, setSaving] = useState(false)
  // Recurso ya asignado a otro paquete: el builder lo detecta contra el inventario.
  const [conflictoComponentes, setConflictoComponentes] = useState(false)

  // Clientes de la base (con sus contactos embebidos) para vincular en vez de escribir a mano.
  const { data: clientes = [] } = useQuery<Cliente[]>({
    queryKey: ['clientes'],
    queryFn: () => api.get<Cliente[]>('/api/clientes'),
    staleTime: 5 * 60 * 1000,
  })
  const [clienteQuery, setClienteQuery] = useState('')
  const [clienteOpen, setClienteOpen] = useState(false)
  const [contactoSelId, setContactoSelId] = useState<number | null>(null)

  const [form, setForm] = useState({
    // Cliente
    cliente_id: null as number | null,
    razon_social: '',
    nombre: '', email: '', whatsapp: '', ruc: '',
    // Tipo
    tipo_paquete: 'cloud' as HostingTipoPaquete,
    // Comercial
    plan_nombre: 'Servicio gestionado', total: '', periodo: 'mensual', moneda: 'PEN',
    // Componentes (pre-siembra el borde de Cloudflare = punto de control)
    componentes: [nuevoComponente('cloudflare_edge')] as HostingComponente[],
    dominio: '',
    // Cobranza
    fecha_vencimiento: '', fecha_corte: '',
    suspension_modo: '' as '' | 'auto' | 'manual', // '' = derivar de los componentes
    notas: '',
  })
  const set = (patch: Partial<typeof form>) => setForm(f => ({ ...f, ...patch }))

  // ─── Vinculación con un cliente existente de la base ───
  const clienteSel = useMemo(() => clientes.find(c => c.id === form.cliente_id) || null, [clientes, form.cliente_id])
  const clientesFiltrados = useMemo(() => {
    const q = clienteQuery.trim().toLowerCase()
    const base = q
      ? clientes.filter(c => c.empresa?.toLowerCase().includes(q) || (c.ruc_dni || '').toLowerCase().includes(q))
      : clientes
    return base.slice(0, 8)
  }, [clientes, clienteQuery])

  // Al elegir un contacto de la empresa, se prellenan (editables) los datos del paquete.
  const aplicarContacto = (ct: ContactoVinculado | null) => {
    setContactoSelId(ct?.id ?? null)
    set({ nombre: ct ? nombreContacto(ct) : '', email: ct?.email ?? '', whatsapp: ct?.telefono ?? '' })
  }
  const seleccionarCliente = (c: Cliente) => {
    const ct = contactoFacturacionDe(c)
    setContactoSelId(ct?.id ?? null)
    set({
      cliente_id: c.id,
      razon_social: c.razon_social ?? '',
      ruc: c.ruc_dni ?? '',
      nombre: ct ? nombreContacto(ct) : c.empresa,
      email: ct?.email ?? '',
      whatsapp: ct?.telefono ?? '',
    })
    setClienteOpen(false)
    setClienteQuery('')
  }
  const desvincularCliente = () => {
    setContactoSelId(null)
    set({ cliente_id: null, razon_social: '' })
  }

  // Corte manual sugerido si algún componente es Supabase (pausa manual del proyecto).
  const modoDerivado: 'auto' | 'manual' = useMemo(
    () => (form.componentes.some(c => c.proveedor === 'supabase_project') ? 'manual' : 'auto'),
    [form.componentes],
  )
  const modoEfectivo = form.suspension_modo || modoDerivado

  // Validación por paso — habilita "Siguiente".
  const pasoValido = (p: number): boolean => {
    if (p === 0) return !!form.nombre.trim() && emailValido(form.email.trim())
    if (p === 3) return form.componentes.length > 0 && !conflictoComponentes && form.componentes.every(c => (c.recurso_ref || '').trim() !== '')
    return true
  }

  const siguiente = () => { if (pasoValido(paso)) setPaso(p => Math.min(p + 1, PASOS.length - 1)) }
  const anterior = () => setPaso(p => Math.max(p - 1, 0))

  const crear = async () => {
    setSaving(true)
    try {
      await api.post('/api/hosting/pedidos', {
        cliente_id: form.cliente_id,
        nombre: form.nombre.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
        ruc: form.ruc.trim() || null,
        razon_social: form.razon_social.trim() || null,
        tipo_paquete: form.tipo_paquete,
        plan_nombre: form.plan_nombre.trim() || 'Servicio gestionado',
        total: Number(form.total) || 0,
        periodo: form.periodo,
        moneda: form.moneda,
        componentes: form.componentes.map(c => ({ ...c, recurso_ref: (c.recurso_ref || '').trim() })),
        dominio: form.dominio.trim() || null,
        fecha_vencimiento: form.fecha_vencimiento || null,
        fecha_corte: form.fecha_corte || null,
        suspension_modo: modoEfectivo,
        notas: form.notas.trim() || null,
      })
      showToast('Paquete creado', 'success')
      queryClient.invalidateQueries({ queryKey: ['hosting-pedidos'] })
      navigate('/hosting')
    } catch (err: any) {
      showToast(err.message || 'Error al crear el paquete', 'error')
    } finally {
      setSaving(false)
    }
  }

  const labelProveedor = (v?: string) => PROVEEDOR_OPCIONES.find(o => o.value === v)?.label || v || '—'

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHeader
        breadcrumbs={[{ label: 'Hosting', to: '/hosting' }, { label: 'Nuevo servicio' }]}
        subtitle="Un paquete con sus componentes: se cobra y se cancela como unidad."
      />

      {/* Stepper */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, flexWrap: 'wrap' }}>
        {PASOS.map((p, i) => (
          <button
            key={p}
            onClick={() => i <= paso && setPaso(i)}
            disabled={i > paso}
            className={`btn btn-sm ${i === paso ? 'btn-primary' : 'btn-ghost'}`}
            style={{ opacity: i > paso ? 0.5 : 1, cursor: i > paso ? 'default' : 'pointer' }}
          >
            <span style={{ opacity: 0.6, marginRight: 6 }}>{i + 1}</span>{p}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 24 }}>
        {/* Paso 0 — Cliente */}
        {paso === 0 && (
          <div style={{ display: 'grid', gap: 14 }}>
            {/* Vincular cliente existente de la base (fuente de verdad) */}
            <div style={{ position: 'relative' }}>
              <label className="form-label">Cliente</label>
              {clienteSel ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--primary, #342CDE)', borderRadius: 10, background: 'var(--primary-soft, color-mix(in srgb, var(--brand-500) 8%, transparent))' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{clienteSel.empresa}</div>
                    {clienteSel.ruc_dni && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>RUC {clienteSel.ruc_dni}</div>}
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={desvincularCliente}>Cambiar</button>
                </div>
              ) : (
                <>
                  <input
                    className="form-input"
                    placeholder="Buscar por empresa o RUC…"
                    value={clienteQuery}
                    onChange={e => { setClienteQuery(e.target.value); setClienteOpen(true) }}
                    onFocus={() => setClienteOpen(true)}
                    onBlur={() => setTimeout(() => setClienteOpen(false), 150)}
                  />
                  {clienteOpen && clientesFiltrados.length > 0 && (
                    <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--surface, #1a1a2e)', border: '1px solid var(--border)', borderRadius: 10, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(var(--shadow-rgb), calc(.28 * var(--shadow-k)))' }}>
                      {clientesFiltrados.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onMouseDown={() => seleccionarCliente(c)}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', color: 'var(--text-primary)' }}
                        >
                          <span style={{ fontWeight: 500 }}>{c.empresa}</span>
                          {c.ruc_dni && <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginLeft: 8 }}>RUC {c.ruc_dni}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  <small style={{ color: 'var(--text-muted)' }}>Búscalo en la base, o completa los datos abajo para un cliente nuevo.</small>
                </>
              )}
            </div>

            {/* Contacto de la empresa vinculada (fuente de verdad: la relación contacto_empresa) */}
            {clienteSel && (clienteSel.contactos?.length ?? 0) > 0 && (
              <div>
                <label className="form-label">Contacto de la empresa</label>
                <SelectorContactoEmpresa contactos={clienteSel.contactos!} value={contactoSelId} onSelect={aplicarContacto} />
              </div>
            )}

            {/* Datos del contacto (prellenados desde la base, editables) */}
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.nombre} onChange={e => set({ nombre: e.target.value })} placeholder="Nombre del contacto" />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => set({ email: e.target.value })} placeholder="correo@ejemplo.com" />
              </div>
            </div>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label className="form-label">WhatsApp</label>
                <input className="form-input" value={form.whatsapp} onChange={e => set({ whatsapp: e.target.value })} placeholder="51999999999" />
              </div>
              <div>
                <label className="form-label">RUC (opcional)</label>
                <input className="form-input" value={form.ruc} onChange={e => set({ ruc: e.target.value })} placeholder="20XXXXXXXXX" />
              </div>
            </div>
          </div>
        )}

        {/* Paso 1 — Tipo de paquete */}
        {paso === 1 && (
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {TIPO_CARDS.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => set({ tipo_paquete: t.value })}
                className="card"
                style={{
                  padding: 18, textAlign: 'left', cursor: 'pointer',
                  border: form.tipo_paquete === t.value ? '2px solid var(--primary, #342CDE)' : '1px solid var(--border)',
                  background: form.tipo_paquete === t.value ? 'var(--primary-soft, color-mix(in srgb, var(--brand-500) 8%, transparent))' : 'transparent',
                }}
              >
                <div style={{ fontSize: '1.6rem', marginBottom: 8 }}>{t.icon}</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.titulo}</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{t.desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* Paso 2 — Comercial */}
        {paso === 2 && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div>
              <label className="form-label">Nombre del plan / servicio</label>
              <input className="form-input" value={form.plan_nombre} onChange={e => set({ plan_nombre: e.target.value })} placeholder="Servicio gestionado" />
            </div>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div>
                <label className="form-label">Monto</label>
                <input className="form-input" type="number" step="0.01" value={form.total} onChange={e => set({ total: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <label className="form-label">Periodo</label>
                <select className="form-input" value={form.periodo} onChange={e => set({ periodo: e.target.value })}>
                  <option value="mensual">Mensual</option>
                  <option value="anual">Anual</option>
                </select>
              </div>
              <div>
                <label className="form-label">Moneda</label>
                <select className="form-input" value={form.moneda} onChange={e => set({ moneda: e.target.value })}>
                  <option value="PEN">PEN (S/)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Paso 3 — Componentes */}
        {paso === 3 && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <label className="form-label">Dominio del paquete (opcional)</label>
              <input className="form-input" value={form.dominio} onChange={e => set({ dominio: e.target.value })} placeholder="cliente.com" />
              <small style={{ color: 'var(--text-muted)' }}>Se bloquea en el borde junto con los hostnames de los componentes edge.</small>
            </div>
            <div>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Componentes del stack</label>
              <ComponentesBuilder
                value={form.componentes}
                onChange={comps => set({ componentes: comps })}
                onConflictoChange={setConflictoComponentes}
              />
            </div>
          </div>
        )}

        {/* Paso 4 — Cobranza & ciclo */}
        {paso === 4 && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label className="form-label">Vencimiento</label>
                <input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e => set({ fecha_vencimiento: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Fecha de corte (opcional)</label>
                <input className="form-input" type="date" value={form.fecha_corte} onChange={e => set({ fecha_corte: e.target.value })} />
                <small style={{ color: 'var(--text-muted)' }}>Si se deja vacío = vencimiento + 7 días de gracia.</small>
              </div>
            </div>
            <div>
              <label className="form-label">Corte por impago</label>
              <select
                className="form-input"
                value={modoEfectivo}
                onChange={e => set({ suspension_modo: e.target.value as 'auto' | 'manual' })}
              >
                <option value="auto">Automático (el cron corta al vencer)</option>
                <option value="manual">Manual (solo avisa, tú cortas)</option>
              </select>
              {!form.suspension_modo && (
                <small style={{ color: 'var(--text-muted)' }}>
                  Sugerido: <strong>{modoDerivado}</strong>{modoDerivado === 'manual' ? ' (hay un componente Supabase que se pausa a mano)' : ''}.
                </small>
              )}
            </div>
            <div>
              <label className="form-label">Notas</label>
              <textarea className="form-input" rows={3} value={form.notas} onChange={e => set({ notas: e.target.value })} placeholder="Detalles internos del paquete…" />
            </div>
          </div>
        )}

        {/* Paso 5 — Resumen */}
        {paso === 5 && (
          <div style={{ display: 'grid', gap: 14 }}>
            {clienteSel && <Resumen label="Empresa" value={`${clienteSel.empresa}${clienteSel.ruc_dni ? ' · RUC ' + clienteSel.ruc_dni : ''}`} />}
            <Resumen label="Contacto" value={`${form.nombre || '—'} · ${form.email || '—'}${form.whatsapp ? ' · ' + form.whatsapp : ''}`} />
            <Resumen label="Tipo de paquete" value={TIPO_CARDS.find(t => t.value === form.tipo_paquete)?.titulo || form.tipo_paquete} />
            <Resumen label="Plan" value={`${form.plan_nombre} · ${form.total || '0'} ${form.moneda} / ${form.periodo}`} />
            <div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 6 }}>Componentes</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {form.componentes.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.9rem' }}>
                    <span className="badge badge-info">{labelProveedor(c.proveedor)}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{c.recurso_ref || <em>sin recurso</em>}</span>
                  </div>
                ))}
              </div>
            </div>
            <Resumen label="Corte por impago" value={modoEfectivo === 'manual' ? 'Manual' : 'Automático'} />
            {form.fecha_vencimiento && <Resumen label="Vencimiento" value={form.fecha_vencimiento} />}
          </div>
        )}

        {/* Navegación */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-ghost" onClick={anterior} disabled={paso === 0 || saving}>← Anterior</button>
          {paso < PASOS.length - 1 ? (
            <button className="btn btn-primary" onClick={siguiente} disabled={!pasoValido(paso)}>Siguiente →</button>
          ) : (
            <button className="btn btn-primary" onClick={crear} disabled={saving || !pasoValido(0)}>
              {saving ? 'Creando…' : 'Crear paquete'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function Resumen({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.95rem' }}>{value}</div>
    </div>
  )
}
